import * as React from 'react'

import { KonvaEventObject } from 'konva/lib/Node'
import { GeoJSONFeature, MapMouseEvent, MapTouchEvent } from 'mapbox-gl'
import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
  when,
} from 'mobx'
import { inject, MobXProviderContext, observer, Provider } from 'mobx-react'
import { Marker } from 'react-map-gl/mapbox'

import { IGeoJson2DGeographicCoordinates, ISiteLocation } from '~/client/graph'
import BaseMapView from '~/client/src/shared/components/BaseMapView/BaseMapView'
import KonvaSitemapItems, {
  MAX_PERCENT,
} from '~/client/src/shared/components/SitemapHelpers/components/KonvaSitemapItems'
import { WorkflowGlyph } from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowIconGroups'
import {
  DRAFT_PIN_FILL,
  getWorkflowStatusPinFill,
  WORKFLOW_PIN_INK,
} from '~/client/src/shared/constants/workflowStatusColors'
import { getWorkflowGlyphPaths } from '~/client/src/shared/constants/workflowTypeGlyphs'
import ICanvasImageCache from '~/client/src/shared/interfaces/ITextboxesCache'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import Markup from '~/client/src/shared/models/Markup'
import Workflow from '~/client/src/shared/models/Workflow'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'

import GlobeView from '../../models/GlobeView'
import { LocationIntegrationType } from '../../models/LocationObjects/LocationIntegration'
import { getSharedProjectName } from '../../models/SharedInfo'
import Sitemap from '../../models/Sitemap'
import WorkflowType from '../../models/WorkflowType'
import CompaniesStore from '../../stores/domain/Companies.store'
import MarkupsStore from '../../stores/domain/Markups.store'
import ProjectsStore from '../../stores/domain/Projects.store'
import WorkflowTypesStore from '../../stores/domain/WorkflowTypes.store'
import EventsStore from '../../stores/EventStore/Events.store'
import InitialState from '../../stores/InitialState'
import BaseLogisticsStore from '../../stores/ui/BaseLogistics.store'
import BaseLogisticsMapStore from '../../stores/ui/BaseLogisticsMap.store'
import ProjectDateStore from '../../stores/ui/ProjectDate.store'
import KonvaPin from '../Konva/KonvaPin'
import { composeLabelGround } from '../MapBoxViewer/labelGround'
import { IGroundContext } from '../MapBoxViewer/labelGroundTone'
import { ATTRIBUTION_TOP_PX } from '../MapBoxViewer/mapAttributionRule'
import {
  LOCATION_MARKERS_LAYER,
  LOCATION_MARKERS_SELECTED_LAYER,
  locationMarkerLayerIds,
  MAPBOX_CLICK_TOLERANCE_PX,
  mapViewItemKey,
} from '../MapBoxViewer/mapboxConstants'
import MapBoxViewerStore from '../MapBoxViewer/MapBoxViewer.store'
import { createMapLongPressRecognizer } from '../MapBoxViewer/mapLongPress'
import { SESSION_MARKUP_PREFIX } from '../MapBoxViewer/markupTextLayerIds'
import { preciseLocationAtCoordinates } from '../MapBoxViewer/preciseLocationAtCoordinates'
import { queryFirstHit } from '../MapBoxViewer/useMapHoverProbe'
import MapCardCarousel from '../MapCardCarousel/MapCardCarousel'
import { MapControls } from '../MapControls'
import { IMarkupLabelPins } from '../MapMarkups/markupLabelPins'
import { markupLabelsFor } from '../MapMarkups/markupLabels'
import MarkupLayer, {
  MARKUP_LABEL_POPUP_OFFSET_PX,
} from '../MapMarkups/MarkupLayer'
import FormMapPopup from '../MapPopups/FormMapPopup'
import LocationMapPopup from '../MapPopups/LocationMapPopup'
import MaturixStationMapPopup from '../MapPopups/MaturixStationMapPopup'
import PermitCard from '../SitemapCards/PermitCard'
import SitemapLocationModal from '../SitemapDraggableModalWrapper/components/SitemapLocationModal'
import SitemapMaturixDraggableModal from '../SitemapDraggableModalWrapper/components/SitemapMaturixStationDraggableModal'
import SitemapPermitsDraggableModal from '../SitemapDraggableModalWrapper/components/SitemapPermitsDraggableModal'
import SitemapElementsWrapper from '../SitemapElementsWrapper'
import DomMovablePin from '../SitemapHelpers/components/DomPins/DomMovablePin'
import DomPin from '../SitemapHelpers/components/DomPins/DomPin'
import GlobeViewFiles from '../SitemapHelpers/components/GlobeViewFiles/GlobeViewFiles'
import GlobeViewItems from '../SitemapHelpers/components/GlobeViewItems'
import GLWorkflowPinMarker from '../SitemapHelpers/components/GLWorkflowPinMarker'
import MapViewItemBase from '../SitemapHelpers/models/MapViewItemBase'
import { isMapPinSelected } from '../SitemapHelpers/utils/mapPinSelection'
import {
  IPlanFootprint,
  planFootprints,
} from '../SitemapHelpers/utils/planFootprints'
import GLSharedPinLayer, {
  CHIP_INTERACTIVE_LAYER_IDS,
  SHARED_PIN_LAYER_IDS,
} from './GLSharedPinLayer'
import GLWorkflowPinLabelLayer, {
  IWorkflowPinLabelPoint,
  WORKFLOW_PIN_LABEL_LAYER_IDS,
} from './GLWorkflowPinLabelLayer'
import GLWorkflowPinLayer, {
  WORKFLOW_PIN_INTERACTIVE_LAYER_IDS,
} from './GLWorkflowPinLayer'
import LocationDrawer from './LocationDrawer'
import LogisticsMapViewSetUpStore from './LogisticsMapViewSetUp.store'
import MapMarkupSessionStore from './MapMarkupSession.store'
import MapMarkupSessionOverlay from './MapMarkupSessionOverlay'
import MobileCreateHint from './MobileCreateHint'
import {
  markerPopupOffset,
  pinPopupOffset,
  popupPinScale,
} from './pinPopupAnchor'
import { buildSharedPinPoints, ISharedPinGroup } from './sharedPinPoints'
import MapObjectStackStore, {
  IMapObjectStackHost,
} from './stack/MapObjectStack.store'
import StackDrawer from './stack/StackDrawer'
import StackMapPopup from './stack/StackMapPopup'
import {
  getMapCreatableWorkflowTypes,
  getMapCreateWorkflowType,
  isComposerCompletable,
  resolveCreationWorkflowType,
} from './workflowCreationPolicy'
import {
  buildWorkflowPinCoordOverrides,
  filterWorkflowsOnMap,
} from './workflowPinAnchors'
import { buildWorkflowPinLabelPoints } from './workflowPinLabelPoints'
import {
  buildWorkflowPinPoints,
  filterPinnedWorkflows,
  iconNameForGroup,
  IWorkflowPinCoordOverride,
  IWorkflowPinPoint,
  ownWorkflowPinCoord,
  statusForGroup,
} from './workflowPinPoints'
import WorkflowTypeCreateBubble from './WorkflowTypeCreateBubble'

// The selected-pin card's own height. It sits on the attribution row, which MapControls adds back.
const CARD_HEIGHT = 168
// In px, not the CSS variable: these overlays are siblings of the element MapControls publishes it
// on, so they cannot read it.
const ABOVE_ATTRIBUTION_PX = ATTRIBUTION_TOP_PX + 4
const ABOVE_CARDS_PX = ATTRIBUTION_TOP_PX + CARD_HEIGHT + 4
const DRAFT_PIN_SOURCE_ID = 'workflow-draft-pin-source'
const DRAFT_PIN_LAYER_ID = 'workflow-draft-pin'

const SAME_POSITION_OFFSET = 20
const OFFSET_Y = 4
// Mobile long-press to create (mirrors desktop right-click): hold this long…

const LONG_PRESS_MS = 500
// …without the map panning more than this (the grabbed point drifts on a pan).
const LONG_PRESS_MOVE_CANCEL_PX = 10
// Frozen empties so the `?? …` fallbacks keep a stable reference (a fresh [] / new Map()
// each render would defeat MarkupLayer's prop memoization during camera moves).
const EMPTY_MARKUPS: Markup[] = []

// Pins before captions: a selected caption draws above every unselected pin, and a hit test
// answers in draw order.
const PIN_HIT_PASSES = [
  WORKFLOW_PIN_INTERACTIVE_LAYER_IDS,
  SHARED_PIN_LAYER_IDS,
  WORKFLOW_PIN_LABEL_LAYER_IDS,
  CHIP_INTERACTIVE_LAYER_IDS,
]
const SHARED_HIT_LAYER_IDS = new Set([
  ...SHARED_PIN_LAYER_IDS,
  ...CHIP_INTERACTIVE_LAYER_IDS,
])
// Everything onMapClick hit-tests. A lifted location draws over all of it, so its click yields
// to any of them instead of opening the location over the workflow the tap was for.
const LOCATION_YIELD_LAYER_IDS = [
  ...PIN_HIT_PASSES.flat(),
  'markup-display-labels',
]
// Everything clickable that draws above a resting markup, so its click never selects the markup.
const MARKUP_YIELD_LAYER_IDS = [
  ...LOCATION_YIELD_LAYER_IDS,
  ...locationMarkerLayerIds(LOCATION_MARKERS_LAYER),
  ...locationMarkerLayerIds(LOCATION_MARKERS_SELECTED_LAYER),
]

const workflowCardKey = (workflow: Workflow): string => workflow.id

interface IProps {
  textboxesCache?: ICanvasImageCache
  store: LogisticsMapViewSetUpStore
  logisticsStore: BaseLogisticsStore
  mapBoxViewerStore: MapBoxViewerStore
  logisticsMapStore: BaseLogisticsMapStore

  openPermit?: (permit: Workflow) => void
  // onSwitched runs only once the switch lands, never when the unsaved-markup guard declines it.
  selectGlobe: (globe: GlobeView, onSwitched?: () => void) => void
  selectSitemap: (sitemap: Sitemap, onSwitched?: () => void) => void

  eventsStore?: EventsStore
  companiesStore?: CompaniesStore
  markupsStore?: MarkupsStore
  projectDateStore?: ProjectDateStore
  workflowTypesStore?: WorkflowTypesStore
  state?: InitialState

  isCompactMode?: boolean
  isMobileEnv?: boolean

  shouldRenderSwipeableCards?: boolean

  globe?: GlobeView
  sitemap?: Sitemap
  projectsStore?: ProjectsStore
  locationAttributesStore?: LocationAttributesStore
  openFormTypeSelectionForm?: () => void
  // Opens the full creation form for a known type. Used when the composer cannot finish
  // the type; without it the map does not offer creation for such a type at all.
  openWorkflowCreationForm?: (permitTypeId: string) => void
  isFormTypeSelectionActive?: boolean
  isPermitCreationActive?: boolean
  // Fullscreen the whole gallery (pill + overlays), not just the map canvas.
  fullscreenTargetRef?: React.RefObject<HTMLElement>
  // The host's own map controls, above Layers.
  mapControlsExtra?: React.ReactNode
}

@inject(
  'state',
  'eventsStore',
  'companiesStore',
  'markupsStore',
  'projectDateStore',
  'workflowTypesStore',
  'projectsStore',
  'locationAttributesStore',
  'isMobileEnv',
)
@observer
export default class LogisticsMapView extends React.Component<IProps> {
  private pressPoint: MapMouseEvent['point'] = null

  public static contextType = MobXProviderContext
  // A clicked shared pin's group. Local, not in the store: these are DTOs from another
  // project, and feeding synthetic models into the store's workflow selection would push
  // them through the list, the gantt and every other consumer of that selection.
  @observable private selectedSharedCoordKey: string = null
  @observable private swipeableItemId: string = null
  @observable private deliverySitemap: BaseMapView

  @observable private containerRef: HTMLElement = null
  @observable private localContext = null
  @observable private localProps: IProps = null
  private disposeMarkupVisibilitySync: () => void = null
  private disposeHandOffRelease: () => void = null
  private disposeSharedSelectionGuard: () => void = null
  private disposeSharedGroupGoneGuard: () => void = null
  private disposeStackSelectionGuard: () => void = null
  private detachStack: () => void = null
  private isUnmounted: boolean = false
  // Keyed by permit so only that permit's popup uses it; other selections fall through.
  @observable private markupPopupAnchor: {
    permitId: string
    coord: IGeoJson2DGeographicCoordinates
    offset: number
  } | null = null

  public constructor(props: IProps) {
    super(props)

    const { mapBoxViewerStore } = props
    this.localProps = props

    mapBoxViewerStore.setViewportFromAddress()
    mapBoxViewerStore.setDefaultMapMode()
    makeObservable(this)
  }

  public UNSAFE_componentWillMount(): void {
    this.localContext = this.context
  }

  public componentDidMount(): void {
    // Bridge the visible workflows to the shared MarkupsStore so the Precise Location
    // modal can scope/label other workflows' markups without coupling to the Logistics stores.
    this.disposeMarkupVisibilitySync = reaction(
      () => this.localProps.logisticsMapStore.filteredPermits,
      permits => {
        const { markupsStore } = this.localProps
        const ids = permits.map(p => p.id)
        markupsStore?.setVisibleWorkflows(
          new Map(permits.map(permit => [permit.id, permit])),
        )
        // Load the visible workflows' markups; evict the ones that left the view.
        markupsStore?.ensureLoadedForSitePermits(ids)
        markupsStore?.retainSitePermits(ids)
      },
      { fireImmediately: true },
    )

    // selectSharedGroup deselects the local selection, but nothing did the reverse: selecting a
    // workflow from a pin, the list, the gantt or the create flow left the shared key set, and
    // both popups rendered stacked over the map. Clearing the key here rather than gating the
    // render keeps the pin's selected art in step with it.
    this.disposeSharedSelectionGuard = reaction(
      () => this.localProps.store.hasWorkflowSelection,
      hasWorkflowSelection => {
        if (hasWorkflowSelection) {
          this.deselectSharedGroup()
          this.stack.close()
        }
      },
    )
    this.disposeSharedGroupGoneGuard = reaction(
      () => !!this.selectedSharedCoordKey && !this.selectedSharedGroup,
      isGone => {
        if (isGone) this.deselectSharedGroup()
      },
    )
    // A location picked anywhere — the side list included — replaces the stack list too.
    this.disposeStackSelectionGuard = reaction(
      () => !!this.localProps.store.selectedAttribute,
      hasSelectedAttribute => {
        if (hasSelectedAttribute) this.stack.close()
      },
    )
    this.detachStack = this.stack.attach(this.stackHost)

    this.disposeHandOffRelease = reaction(
      () => this.localProps.isPermitCreationActive,
      isActive => {
        if (!isActive) {
          this.markupSession?.endHandOff()
        }
      },
    )

    this.localProps.store.setCreateBubbleGate(() => this.canEditMarkupsOnMap)

    // Read at move time: a reaction on this plain InitialState field sees only its mount value.
    if (this.markupSession) {
      this.markupSession.applyHandedOffLocation = coords => {
        const apply =
          this.localProps.eventsStore.appState.applyHandedOffPreciseLocation
        if (!apply) {
          return
        }
        this.seedCreateLocation({ lat: coords.latitude, lng: coords.longitude })
        apply(this.localProps.state.preSelectedSiteLocation.preciseLocation)
      }
    }
  }

  public componentWillUnmount(): void {
    this.localProps.store.setCreateBubbleGate(() => false)
    this.isUnmounted = true
    this.disposeMarkupVisibilitySync?.()
    this.disposeMarkupVisibilitySync = null
    this.disposeSharedSelectionGuard?.()
    this.disposeSharedSelectionGuard = null
    this.disposeSharedGroupGoneGuard?.()
    this.disposeSharedGroupGoneGuard = null
    this.disposeStackSelectionGuard?.()
    this.disposeStackSelectionGuard = null
    this.detachStack?.()
    this.detachStack = null
    this.disposeHandOffRelease?.()
    this.disposeHandOffRelease = null
    // Clear the bridge so the PL modal doesn't scope to a stale set (mirrors mobile).
    this.localProps.markupsStore?.setVisibleWorkflows(null)
    this.markupSession?.dispose()
    this.disposeSelectCreated?.()
    this.longPress.cancel()
  }

  private readonly longPress = createMapLongPressRecognizer({
    delayMs: LONG_PRESS_MS,
    moveCancelPx: LONG_PRESS_MOVE_CANCEL_PX,
    onLongPress: lngLat => this.openCreateBubbleAt(lngLat),
  })

  private get markupSession(): MapMarkupSessionStore {
    return this.localProps.store.markupSession
  }

  // Stable-ref (computed) list for MarkupLayer memoization: the session's drafts in the store,
  // which the session mount draws whenever no editor is attached (a create in flight, a
  // failed submit before the editor reattaches).
  @computed
  private get sessionDraftMarkups(): Markup[] {
    const session = this.markupSession
    const { markupsStore, store } = this.localProps
    if (!session || !markupsStore) {
      return EMPTY_MARKUPS
    }
    return markupsStore.forGlobe(
      markupsStore.getBySitePermitId(session.sessionPermitId),
      store.globeViewControlStore.selectedGlobeViewId,
    )
  }

  public componentDidUpdate(prevProps: Readonly<IProps>): void {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  public render() {
    return (
      <div
        className="full-height full-width relative markup-host-clickable-pins"
        ref={this.setContainerRef}
      >
        {this.renderSitemap()}
        {this.renderMarkupSessionOverlay()}
        {this.renderCreateWorkflowBubble()}
        {this.renderCreateHint()}
        {this.localProps.shouldRenderSwipeableCards && (
          <LocationDrawer
            isOpen={this.isLocationDrawerOpen}
            item={
              this.isLocationDrawerOpen
                ? this.localProps.store.selectedAttribute
                : null
            }
            selectedStartDate={this.localProps.logisticsStore.startDate}
            selectedEndDate={this.localProps.logisticsStore.endDate}
            onCreateWorkflow={this.openFormTypeSelectionFormFromLocation}
            onClose={this.localProps.store.clearSelectedAttribute}
          />
        )}
        {this.isSwipeableCardVisible ? (
          <MapControls.AboveAttribution>
            <MapCardCarousel
              items={this.workflowsOnMap}
              selectedId={this.swipeableItemId}
              keyExtractor={workflowCardKey}
              renderCard={this.renderSwipeableCard}
              onSelectedChange={this.selectSwipeableItem}
              onClose={this.dismissSwipeableCards}
            />
          </MapControls.AboveAttribution>
        ) : (
          this.renderCards
        )}
      </div>
    )
  }

  @computed
  private get mapCreatableWorkflowTypes(): WorkflowType[] {
    return getMapCreatableWorkflowTypes(this.localProps.workflowTypesStore)
  }

  // Default for the entry paths that skip the bubble: markup-first and the move-pin tool.
  @computed
  private get mapCreateWorkflowType(): WorkflowType {
    return getMapCreateWorkflowType(this.localProps.workflowTypesStore)
  }

  @computed
  private get isMapCreationSurface(): boolean {
    const {
      mapBoxViewerStore,
      eventsStore: { appState },
      store,
    } = this.localProps
    return (
      !!this.markupSession &&
      store.isGlobeMode &&
      mapBoxViewerStore.markupPrefs.supportsWorkflowMarkups &&
      !appState.isWorkflowsDisabled
    )
  }

  // No form gates, so a creation handed to the form can still be drawn behind it.
  @computed
  private get canEditMarkupsOnMap(): boolean {
    return this.isMapCreationSurface && !!this.mapCreatableWorkflowTypes.length
  }

  @computed
  private get canCreateOnMap(): boolean {
    const { isFormTypeSelectionActive, isPermitCreationActive } =
      this.localProps
    return (
      this.canEditMarkupsOnMap &&
      !isFormTypeSelectionActive &&
      !isPermitCreationActive
    )
  }

  // Desktop only: mobile unmounts the whole map behind the form.
  @computed
  private get isHandedOffToOpenForm(): boolean {
    return (
      !this.localProps.isMobileEnv &&
      this.canEditMarkupsOnMap &&
      !!this.markupSession?.isHandedOff &&
      !!this.localProps.isPermitCreationActive
    )
  }

  // Gates both the creation overlay (hence the markup editor) and the mobile long-press
  // gesture. Drawing stays available for a type the composer cannot finish, as long as
  // the host gave us somewhere to send that creation; with no route we don't offer it at
  // all rather than let the user draw into a dead end.
  @computed
  private get isWorkflowCreationAvailable(): boolean {
    if (!this.canCreateOnMap) {
      return false
    }
    return (
      this.mapCreatableWorkflowTypes.some(isComposerCompletable) ||
      !!this.localProps.openWorkflowCreationForm
    )
  }

  // The composer would open with Submit permanently disabled, so the drawn creation goes
  // to the full form instead: type, pin and markup buffer all travel through app state,
  // the same channel the legacy bubble already uses for a location.
  @action.bound
  private routeCreationToForm(): void {
    const {
      eventsStore: { appState },
      openWorkflowCreationForm,
    } = this.localProps
    const session = this.markupSession
    // Without a host route, parking a draft id would leave the next form built anywhere
    // to adopt it and flush a foreign buffer.
    if (!openWorkflowCreationForm) {
      // The composer parks its carry plan before handing over, so drop it here too.
      appState.preSelectedWorkflowCarryPlan = null
      return
    }
    const coords = session?.creationCoordinates
    if (coords) {
      this.seedCreateLocation({ lat: coords.latitude, lng: coords.longitude })
    }
    // Read before handOffCreation clears it.
    const workflowTypeId =
      session?.creationWorkflowTypeId ?? this.mapCreateWorkflowType?.id
    appState.preSelectedMarkupDraftId = session?.sessionPermitId ?? null
    // Release the session first: the form owns the create now, and the buffered drafts
    // stay put because it adopted their id.
    session?.handOffCreation()
    openWorkflowCreationForm(workflowTypeId)
  }

  // Which selection the map is showing. The draggable cards and the globe popups each worked these
  // out for themselves, so the two could disagree about what is open.
  @computed
  private get shouldShowWorkflowModal(): boolean {
    // The resolved list, not the intent: both renderers bail out of render() on empty.
    return (
      !!this.localProps.store.selectedWorkflows.length && !!this.containerRef
    )
  }

  // Maturix stations get their own surface, on both renderers.
  @computed
  private get isMaturixAttributeSelected(): boolean {
    return !!this.localProps.store.selectedAttribute?.dataObject?.is(
      LocationIntegrationType.MaturixStation,
    )
  }

  @computed
  private get isSwipeableCardVisible(): boolean {
    // Hidden by default on map open; only a pin selection reveals it.
    return (
      !!this.localProps.shouldRenderSwipeableCards &&
      !this.markupSession?.isInteracting &&
      !!this.swipeableItemId &&
      // Without this the strip outlives its card and slides to an unrelated one.
      this.workflowsOnMap.some(w => w.id === this.swipeableItemId)
    )
  }

  // Mobile's stand-in for the desktop location popup, which the swipeable layout skips: without
  // it a tapped location, or a stack row, selected nothing anyone could see.
  @computed
  private get isLocationDrawerOpen(): boolean {
    const { shouldRenderSwipeableCards, store } = this.localProps
    return (
      !!shouldRenderSwipeableCards &&
      store.shouldShowSelectedAttribute &&
      !this.isMaturixAttributeSelected &&
      !store.isMapFirstCreationActive &&
      !this.markupSession?.isInteracting
    )
  }

  @action.bound
  private dismissSwipeableCards(): void {
    this.swipeableItemId = null
    this.localProps.mapBoxViewerStore.selectedMarkerItemId = null
    this.localProps.store.deselectAll()
  }

  private renderMarkupSessionOverlay(): JSX.Element {
    return this.isWorkflowCreationAvailable || this.isHandedOffToOpenForm ? (
      <MapMarkupSessionOverlay
        session={this.markupSession}
        workflowType={this.creationWorkflowType}
        onWorkflowCreated={this.selectCreatedWorkflow}
        onRouteCreationToForm={this.routeCreationToForm}
        yieldToLayerIds={MARKUP_YIELD_LAYER_IDS}
      />
    ) : null
  }

  private disposeSelectCreated: () => void = null

  // `when`: the created permit can reach workflowsOnMap a tick after the session
  // ends; a submit can finish after unmount, so bail or the reaction leaks.
  private selectCreatedWorkflow = (permitId: string): void => {
    if (!permitId || this.isUnmounted) {
      return
    }
    this.disposeSelectCreated?.()
    this.disposeSelectCreated = when(
      () => this.workflowsOnMap.some(permit => permit.id === permitId),
      () => {
        const permit = this.workflowsOnMap.find(p => p.id === permitId)
        if (permit) {
          this.onPreciseLocationPermitClick([permit])
          // Desktop recenters only when the pin is off-screen (avoids a jarring
          // jump); mobile always centers, clear of the header/card chrome.
          this.markupSession?.centerOnCreatedWorkflow(!this.props.isMobileEnv)
        }
      },
    )
  }

  // Hidden once a creation or move-mode is active — the flow is then self-evident.
  private renderCreateHint(): JSX.Element {
    if (
      !this.props.isMobileEnv ||
      !this.isWorkflowCreationAvailable ||
      this.localProps.store.isMapFirstCreationActive ||
      this.markupSession?.isMovingPin
    ) {
      return null
    }
    // Sits above the swipeable card strip while it's up.
    return (
      <div
        className="tw:pointer-events-auto tw:absolute tw:left-2.5 tw:z-[10]"
        style={{
          bottom: this.isSwipeableCardVisible
            ? ABOVE_CARDS_PX
            : ABOVE_ATTRIBUTION_PX,
        }}
      >
        <MobileCreateHint />
      </div>
    )
  }

  @action.bound
  private startWorkflowCreationFromBubble(workflowTypeId: string): void {
    const { store, state } = this.localProps
    const coords = state.preSelectedSiteLocation?.preciseLocation
    store.setShouldShowCreateWorkflowBubble(false)
    // Clear the seed — a lingering one re-shows the placement ghost after
    // the creation is discarded (renderPreSelectedPin only hides it mid-session).
    state.preSelectedSiteLocation = null
    if (coords) {
      // Before startCreation: the overlay seeds the default off this on creation start.
      this.markupSession?.setCreationWorkflowType(workflowTypeId)
      // Explicit pin drop: the pin stays put; markups never pull it (Scenario A).
      this.markupSession?.startCreation(
        {
          latitude: coords.latitude,
          longitude: coords.longitude,
        },
        true,
        { bearing: coords.bearing, pitch: coords.pitch },
      )
    }
  }

  private get renderCards(): JSX.Element {
    const { shouldRenderSwipeableCards } = this.localProps

    if (shouldRenderSwipeableCards) {
      return
    }

    return (
      <>{!this.localProps.store.isGlobeMode && this.renderDraggableCards}</>
    )
  }

  private get renderDraggableCards(): JSX.Element {
    const {
      openPermit,
      logisticsStore: { startDate, endDate },
    } = this.localProps

    const {
      selectedWorkflows = [],
      leftOffset,
      topOffset,
      selectedAttribute,
      shouldShowSelectedAttribute,
      displayedPermitId,
      deselectAll,
    } = this.localProps.store

    return (
      <>
        {this.shouldShowWorkflowModal && (
          <SitemapPermitsDraggableModal
            item={selectedAttribute}
            containerRef={this.containerRef}
            selectedSitePermits={selectedWorkflows}
            selectedPermitId={displayedPermitId}
            openPermit={openPermit}
            onClose={deselectAll}
            topOffset={topOffset}
            leftOffset={leftOffset}
          />
        )}
        {shouldShowSelectedAttribute &&
          (this.isMaturixAttributeSelected ? (
            <SitemapMaturixDraggableModal
              item={selectedAttribute}
              containerRef={this.containerRef}
              onClose={deselectAll}
              topOffset={topOffset}
              leftOffset={leftOffset}
            />
          ) : (
            <SitemapLocationModal
              item={selectedAttribute}
              topOffset={topOffset}
              leftOffset={leftOffset}
              selectedStartDate={startDate}
              selectedEndDate={endDate}
              containerRef={this.containerRef}
              onClose={deselectAll}
              onPlusButtonClick={this.openFormTypeSelectionFormFromLocation}
            />
          ))}
      </>
    )
  }

  private openFormTypeSelectionForm = (): void => {
    const { store, openFormTypeSelectionForm } = this.localProps
    runInAction(() => {
      store.selectedAttribute = null
    })
    store.setShouldShowCreateWorkflowBubble(false)
    openFormTypeSelectionForm?.()
  }

  @action.bound
  private openFormTypeSelectionFormFromLocation() {
    const { selectedAttribute } = this.localProps.store
    // A sheet sliding shut still shows its button after the selection has gone.
    if (!selectedAttribute?.dataObject) return
    this.openFormTypeSelectionForm()
    this.localProps.state.preSelectedSiteLocation = {
      id: selectedAttribute.id,
      type: selectedAttribute.dataObject.type,
    }
  }

  private get renderGlobePopups(): JSX.Element {
    const {
      openPermit,
      logisticsStore: { startDate, endDate },
      shouldRenderSwipeableCards,
    } = this.localProps

    if (shouldRenderSwipeableCards) {
      return
    }

    const {
      selectedWorkflows = [],
      selectedAttribute,
      displayedPermitId,
      shouldShowSelectedAttribute,
      deselectAll,
    } = this.localProps.store

    // Popup opened from a markup label anchors at the label coord; lift its tip clear
    // of the label (same as the Precise Location info card) so it grows from the top.
    const markupAnchor = this.markupAnchorFor(selectedWorkflows[0]?.id)
    const pinCoords = this.workflowPinCoordOverrides
    const selectedPermit = selectedWorkflows[0]
    const selectedOverride = pinCoords.get(selectedPermit?.id)
    // A pinned (precise-located or anchor-resolved) permit's popup grows from the pin.
    const opensFromPin =
      !!ownWorkflowPinCoord(selectedPermit) || pinCoords.has(selectedPermit?.id)
    const { globeViewStyle } = this.localProps.mapBoxViewerStore
    const isSharedGroupOpen = !!this.selectedSharedWorkflows.length
    const pinScale = popupPinScale(
      this.shouldShowWorkflowModal || isSharedGroupOpen,
      globeViewStyle.isZoomScalingEnabled,
      () => this.localProps.mapBoxViewerStore.viewport?.zoom,
    )
    const selectedPinPoint = this.workflowPinData.points.find(p => p.isSelected)
    const localPinPopupOffset = pinPopupOffset(
      selectedPinPoint?.count,
      !!selectedPinPoint?.anchored,
      pinScale,
    )
    const sharedPinPopupOffset = pinPopupOffset(
      this.selectedSharedPoint?.count,
      false,
      pinScale,
    )

    return (
      <>
        {!!this.selectedSharedWorkflows.length && (
          <FormMapPopup
            // No local item behind a shared pin: its location lives in another project, so
            // the popup is anchored by coordinate and names itself from the workflows.
            item={null}
            selectedSitePermits={this.selectedSharedWorkflows}
            selectedPermitId={null}
            coordOverride={{
              latitude: this.selectedSharedPoint.latitude,
              longitude: this.selectedSharedPoint.longitude,
            }}
            offset={sharedPinPopupOffset}
            sharedFromProjectName={this.selectedSharedProjectName}
            openPermit={this.openSharedWorkflow}
            onClose={this.deselectSharedGroup}
          />
        )}
        {this.shouldShowWorkflowModal && (
          <FormMapPopup
            item={selectedAttribute}
            selectedSitePermits={selectedWorkflows}
            selectedPermitId={displayedPermitId}
            coordOverride={markupAnchor ?? selectedOverride?.coord}
            offset={
              markupAnchor
                ? this.markupPopupAnchor?.offset
                : opensFromPin
                  ? localPinPopupOffset
                  : undefined
            }
            openPermit={openPermit}
            onClose={deselectAll}
            // Tapping this workflow's own pin or label must not deselect it on the way to
            // reselecting it. onMapClick deselects when a click hits nothing instead.
            closeOnMapClick={false}
          />
        )}
        {this.stack.isShown && (
          <StackMapPopup
            latitude={this.stack.open.members.latitude}
            longitude={this.stack.open.members.longitude}
            offset={markerPopupOffset(
              popupPinScale(
                true,
                globeViewStyle.isZoomScalingEnabled,
                () => this.localProps.mapBoxViewerStore.viewport?.zoom,
              ),
            )}
            {...this.stack.listProps}
          />
        )}
        {shouldShowSelectedAttribute &&
          (this.isMaturixAttributeSelected ? (
            <MaturixStationMapPopup
              item={selectedAttribute}
              onClose={deselectAll}
            />
          ) : (
            <LocationMapPopup
              item={selectedAttribute}
              offset={markerPopupOffset(
                popupPinScale(
                  true,
                  globeViewStyle.isZoomScalingEnabled,
                  () => this.localProps.mapBoxViewerStore.viewport?.zoom,
                ),
              )}
              selectedStartDate={startDate}
              selectedEndDate={endDate}
              onClose={deselectAll}
              onPlusButtonClick={this.openFormTypeSelectionFormFromLocation}
            />
          ))}
      </>
    )
  }

  @action.bound
  private onMouseDown(event: MapMouseEvent): void {
    // Always-on editor: right-click still creates while idle-select; only a live
    // drawing tool / in-progress creation owns the map gestures.
    if (this.markupSession?.isInteracting) {
      return
    }
    this.localProps.store.setShouldShowCreateWorkflowBubble(false)
    if (
      !this.localProps.isFormTypeSelectionActive &&
      !this.localProps.isPermitCreationActive
    ) {
      this.props.state.preSelectedSiteLocation = null
    }
    this.pressPoint = event.point
  }

  // Right CLICK, never a right drag — compared in SCREEN space against the press rather than
  // trusting mapbox to withhold the event: platforms disagree on whether contextmenu arrives
  // before or after mouseup, and only the earlier ordering lets mapbox drop it after a rotate.
  // A press-less contextmenu (Shift+F10, Android long-press) has no point to match and is ignored,
  // which also leaves touch to the long-press recognizer.
  @action.bound
  private onContextMenu(event: MapMouseEvent): void {
    const pressPoint = this.pressPoint
    this.pressPoint = null
    if (this.markupSession?.isInteracting || !pressPoint) {
      return
    }
    if (pressPoint.dist(event.point) > MAPBOX_CLICK_TOLERANCE_PX) {
      return
    }
    // The states onMouseDown preserves preSelectedSiteLocation for: mid-creation a right-click
    // must not reseat the location being placed.
    if (
      this.localProps.isFormTypeSelectionActive ||
      this.localProps.isPermitCreationActive
    ) {
      return
    }
    this.openCreateBubbleAt(event.lngLat)
  }

  @action.bound
  private seedCreateLocation(lngLat: { lat: number; lng: number }): void {
    this.props.state.preSelectedSiteLocation = {
      id: null,
      type: null,
      preciseLocation: preciseLocationAtCoordinates(
        this.props.mapBoxViewerStore,
        { latitude: lngLat.lat, longitude: lngLat.lng },
      ),
    } as ISiteLocation
  }

  @action.bound
  private openCreateBubbleAt(lngLat: { lat: number; lng: number }): void {
    // Picker fallback only where the map could create but no type takes a pin.
    if (!this.isWorkflowCreationAvailable) {
      if (
        !this.isMapCreationSurface ||
        this.mapCreatableWorkflowTypes.length ||
        !this.localProps.openFormTypeSelectionForm
      ) {
        return
      }
      this.seedCreateLocation(lngLat)
      this.openFormTypeSelectionForm()
      return
    }
    // The bubble's pick no-ops until the editor mounts.
    if (!this.markupSession?.isEditorMounted) {
      return
    }
    this.seedCreateLocation(lngLat)
    this.localProps.store.setShouldShowCreateWorkflowBubble(true)
  }

  // A single-finger hold that doesn't pan opens the same bubble as desktop right-click.
  @action.bound
  private onMapTouchStart(event: MapTouchEvent): void {
    this.longPress.cancel()
    if (
      this.markupSession?.isInteracting ||
      event.originalEvent.touches.length !== 1
    ) {
      return
    }
    // Marker touches (create bubble, pins) bubble up to mapbox's container
    // touchstart; dismissing or starting a long-press here kills the bubble's tap.
    const target = event.originalEvent.target as HTMLElement | null
    if (target?.closest('.mapboxgl-marker')) {
      return
    }
    // A new map tap dismisses a stale create bubble + pin (desktop clears on mousedown).
    this.localProps.store.setShouldShowCreateWorkflowBubble(false)
    this.props.state.preSelectedSiteLocation = null
    this.longPress.onTouchStart(event)
  }

  private onMapTouchEnd = (): void => {
    this.longPress.cancel()
  }

  @action
  private onMapClick = (e: MapMouseEvent): void => {
    // Only an active drawing gesture claims the click; with the composer open but
    // idle, sibling pins stay selectable mid-creation.
    if (this.markupSession && !this.markupSession.isEditorIdle) {
      return
    }
    // Any click puts the stack list away; a click on a stacked marker reopens it after this.
    this.stack.close()
    const map = e.target
    // The label and its chip carry their pin's coordKey, so tapping either opens the same
    // workflow. Workflow pins win over shared ones at the same spot.
    const isShared = (hit: GeoJSONFeature): boolean =>
      SHARED_HIT_LAYER_IDS.has(hit.layer?.id)
    const hit = queryFirstHit(map, e.point, PIN_HIT_PASSES, candidate => {
      const key = candidate.properties?.coordKey
      return isShared(candidate)
        ? this.sharedPinData.groupsByCoordKey.has(key)
        : !!this.workflowPinData.workflowsByCoordKey.get(key)?.length
    })
    if (hit) {
      const coordKey = hit.properties.coordKey as string
      if (isShared(hit)) {
        this.selectSharedGroup(coordKey)
      } else {
        this.onPreciseLocationPermitClick(
          this.workflowPinData.workflowsByCoordKey.get(coordKey),
        )
      }
      return
    }
    // Markup labels are GL symbols, not DOM markers — their taps must be hit-tested here.
    if (map.getLayer('markup-display-labels')) {
      const label = map.queryRenderedFeatures(e.point, {
        layers: ['markup-display-labels'],
      })[0]
      if (label?.properties?.markupId && label.geometry.type === 'Point') {
        const [longitude, latitude] = label.geometry.coordinates
        this.selectPermitFromMarkupLabel(label.properties.markupId, {
          longitude,
          latitude,
        })
        return
      }
    }
    // Compact maps get the hit-tests only; they never had whiteboard-deselect. They DID have
    // the popup close on a map tap though, from mapbox's own closeOnClick, and giving that up
    // left the popups with no dismissal but their X on mobile. These two restore exactly what
    // closeOnClick did — close an open popup, do nothing otherwise — without bringing
    // whiteboard-deselect along with it. Both, because each of the two popups has its own
    // selection: the permit deselect no-ops unless a permit popup is showing.
    if (this.localProps.isCompactMode) {
      this.localProps.store.deselectWorkflows()
      this.deselectSharedGroup()
      return
    }
    // Nothing under the pointer: this is the whiteboard click that clears the board. It
    // also does the job mapbox's closeOnClick used to do for the workflow popup, which had
    // to give it up — see closeOnMapClick.
    this.localProps.store.deselectAll()
  }

  private renderSitemap(): JSX.Element {
    const {
      store: { isGlobeMode, sitemapUrl, deselectAll },
      mapBoxViewerStore,
      eventsStore: { appState },
      globe,
      sitemap,
      isCompactMode,
    } = this.props
    const disableMapEvents = isCompactMode || appState.isWorkflowsDisabled

    return (
      <>
        <BaseMapView
          globe={globe}
          selectedSitemap={sitemap}
          sitemapUrl={sitemapUrl}
          isDraggable={true}
          onWhiteboardClick={deselectAll}
          onTouch={this.onSitemapTouch}
          mapBoxViewerStore={mapBoxViewerStore}
          mapEvents={{
            onMouseDown: disableMapEvents ? null : this.onMouseDown,
            onContextMenu: disableMapEvents ? null : this.onContextMenu,
            // Selection tap must work even in compact mode: GL pins have no DOM-marker
            // tap fallback; mapbox synthesizes this click only for a real tap, not a pan.
            onMapClick: appState.isWorkflowsDisabled ? null : this.onMapClick,
            // Always wired (touch never fires for mouse users); gating on this.props
            // here read stale values — the handler scopes itself via localProps.
            onTouchStart: this.onMapTouchStart,
            onTouchEnd: this.onMapTouchEnd,
          }}
          isLogisticsView={true}
          projectAddress={appState.projectAddress}
          isMobileEnv={this.props.isMobileEnv}
          ref={this.setDeliverySitemap}
          fullscreenTargetRef={this.props.fullscreenTargetRef}
          mapControlsExtra={this.props.mapControlsExtra}
        >
          {({ width, height }) => {
            if (!this.deliverySitemap) {
              return null
            }
            return (
              <Provider {...this.localContext}>
                {isGlobeMode
                  ? this.renderGlobeViewChildren()
                  : this.renderSitemapChildren(width, height)}
              </Provider>
            )
          }}
        </BaseMapView>
      </>
    )
  }

  private setDeliverySitemap = ref => {
    this.deliverySitemap = ref
  }

  // Stable-ref data for MarkupLayer: recomputes only when observables change,
  // not per camera frame, so the layer skips re-render during pan/tilt/rotate.
  @computed
  private get markupLabels(): Map<string, string> {
    return markupLabelsFor(this.localProps.markupsStore?.visibleWorkflows)
  }

  // Composited from the scene — see labelGroundTone for why reading the canvas could never work.
  // Where plan sheets sit, so labels standing on paper can be plated. Mirrors the guards
  // GlobeViewPlan renders behind, since a sheet it does not draw is not ground.
  @computed
  private get planFootprints(): IPlanFootprint[] {
    return planFootprints(
      this.localProps.mapBoxViewerStore.sitemapWithBasemapsOnGlobe ?? [],
    )
  }

  // Desktop only, and only while the card is shown: the swipeable cards own no map item.
  @computed
  private get liftedLocationItemId(): string | undefined {
    const { store, shouldRenderSwipeableCards } = this.localProps
    return !shouldRenderSwipeableCards && store.shouldShowSelectedAttribute
      ? mapViewItemKey(store.selectedAttribute.id)
      : undefined
  }

  @computed
  private get labelGround(): IGroundContext {
    const { markupsStore, mapBoxViewerStore, store } = this.localProps
    const markups =
      markupsStore?.visibleMarkupsForGlobe(
        store.globeViewControlStore.selectedGlobeViewId,
      ) ?? EMPTY_MARKUPS
    return composeLabelGround(
      mapBoxViewerStore.isSatelliteActive,
      mapBoxViewerStore.displayedGlobeViewItems ?? [],
      markups,
    )
  }

  // Which pin group the pointer is over, lifted out of GLWorkflowPinLayer so the label
  // layer beside it can wear the same highlight.
  @observable private hoveredPinCoordKey: string = null

  // Locations on one coordinate, listed from the marker that stands for them. The map's store
  // keeps the stack; this view lends it what only the drawn map knows.
  private get stack(): MapObjectStackStore {
    return this.localProps.store.stack
  }

  private readonly stackHost: IMapObjectStackHost = {
    getLocation: id =>
      this.localProps.locationAttributesStore?.getById(id) as LocationBase,
    workflowsAt: coordKey =>
      this.workflowPinData.workflowsByCoordKey.get(coordKey) ?? [],
    // Mobile has no surface for a shared workflow, so its row would select nothing visible.
    sharedWorkflowsAt: coordKey =>
      this.localProps.shouldRenderSwipeableCards
        ? []
        : (
            this.sharedPinData.groupsByCoordKey.get(coordKey)?.workflows ?? []
          ).map(dto => Workflow.fromSharedPermitDto(dto)),
    selectLocation: itemId => {
      const item =
        this.localProps.mapBoxViewerStore.displayedGlobeViewItems?.find(
          i => i.id === itemId,
        )
      if (item) this.selectSitemapItem(item)
    },
    selectWorkflow: workflow => this.onPreciseLocationPermitClick([workflow]),
    selectSharedGroup: coordKey => this.selectSharedGroup(coordKey),
    isLocationOnMap: id =>
      !!this.localProps.mapBoxViewerStore.displayedGlobeViewItems?.some(
        item => item.id === id,
      ),
    isMapSwitchLocked: () => {
      const { store, mapBoxViewerStore } = this.localProps
      return (
        !!store.globeViewControlStore.selectedGlobeViewId &&
        !mapBoxViewerStore.isStyleLoaded
      )
    },
    taggedMapsOf: location => {
      const { store, selectGlobe, selectSitemap } = this.localProps
      return store.linkedMapsOf(location).map(linked => ({
        id: linked.id,
        name: linked.name,
        open: () =>
          store.openLinkedMap(linked, location.id, {
            selectGlobe,
            selectSitemap,
          }),
      }))
    },
    onScreenMapId: () => this.localProps.store.onScreenMapId,
    isEditorIdle: () => !this.markupSession || this.markupSession.isEditorIdle,
  }

  @action.bound
  private setHoveredPinCoordKey(coordKey: string): void {
    this.hoveredPinCoordKey = coordKey
  }

  @computed
  private get workflowPinLabelData(): {
    points: IWorkflowPinLabelPoint[]
    logoUrls: Map<string, string>
  } {
    const { companiesStore, projectDateStore, shouldRenderSwipeableCards } =
      this.localProps
    const { selectedWorkflows = [], displayedPermitId } = this.localProps.store
    return buildWorkflowPinLabelPoints(this.pinnedWorkflows, {
      coordOverrides: this.workflowPinCoordOverrides,
      anchorCircleShownIds: this.localProps.store.anchorCircleShownIds,
      getMonthAndDay: projectDateStore?.getMonthAndDayToDisplay,
      getCompany: companyId => companiesStore?.getCompanyById(companyId),
      // The same predicate the pin itself uses, so a pin and its label can never disagree
      // about selection — it is a superset of activeWorkflowIds, which reads selectedWorkflows
      // alone and would miss a workflow opened from the details panel.
      isSelected: group =>
        isMapPinSelected({
          shouldRenderSwipeableCards,
          swipeableItemId: this.swipeableItemId,
          swipeableEntities: group,
          selectedEntities: selectedWorkflows,
          displayedEntityId: displayedPermitId,
        }),
    })
  }

  @computed
  private get activeWorkflowIds(): Set<string> {
    return new Set(
      this.localProps.store.selectedWorkflows?.map(permit => permit.id) ?? [],
    )
  }

  private renderGlobeViewChildren = () => {
    const {
      mapBoxViewerStore,
      logisticsStore: { startDate, endDate },
      markupsStore,
      store,
    } = this.localProps
    const { displayedGlobeViewItems } = mapBoxViewerStore
    const selectedGlobeViewId = store.globeViewControlStore.selectedGlobeViewId

    return (
      <>
        <GlobeViewFiles
          mapBoxViewerStore={mapBoxViewerStore}
          sitemapWithBasemaps={mapBoxViewerStore.sitemapWithBasemapsOnGlobe}
          mapFilesOnGlobe={mapBoxViewerStore.mapFilesOnGlobe}
        />
        <GlobeViewItems
          items={displayedGlobeViewItems || []}
          onSelectItem={this.selectSitemapItem}
          ground={this.labelGround}
          planFootprints={this.planFootprints}
          liftsSelection
          liftedItemId={this.liftedLocationItemId}
          yieldToLayerIds={LOCATION_YIELD_LAYER_IDS}
          onClickStack={this.stack.openOf}
          isDisabled={!!this.markupSession && !this.markupSession.isEditorIdle}
          mapBoxViewerStore={mapBoxViewerStore}
          selectedStartDate={startDate}
          selectedEndDate={endDate}
        />
        <MarkupLayer
          isZoomScalingEnabled={
            mapBoxViewerStore.globeViewStyle.isZoomScalingEnabled
          }
          markups={
            markupsStore?.visibleMarkupsForGlobe(selectedGlobeViewId) ??
            EMPTY_MARKUPS
          }
          visible={
            mapBoxViewerStore.markupPrefs.supportsWorkflowMarkups &&
            mapBoxViewerStore.markupPrefs.showWorkflowMarkups
          }
          // Gated by the per-user "Show association labels" Map Settings toggle.
          labels={this.markupLabels}
          showLabels={
            mapBoxViewerStore.markupPrefs.isWorkflowMarkupLabelsVisible
          }
          activeWorkflowIds={this.activeWorkflowIds}
          ground={this.labelGround}
          allowOverlap={mapBoxViewerStore.globeViewStyle.isLabelOverlapEnabled}
          onHoverChange={mapBoxViewerStore.setOverlayPointerHovered}
          interactive={!this.markupSession || this.markupSession.isEditorIdle}
          pins={this.markupLabelPins}
        />
        {/* Draft markups of an in-progress creation, dimmed until submit
            solidifies them (flushPending) or cancel discards them. */}
        <MarkupLayer
          isZoomScalingEnabled={
            mapBoxViewerStore.globeViewStyle.isZoomScalingEnabled
          }
          idPrefix="markup-draft"
          ground={this.labelGround}
          muted
          markups={
            markupsStore?.draftMarkupsForGlobe(
              selectedGlobeViewId,
              // The live session's drafts render via terra-draw while the
              // editor is mounted, and via the solid layer below while
              // submitting; skip them here to avoid a double/dimmed render.
              this.markupSession?.isActive
                ? this.markupSession.sessionPermitId
                : undefined,
            ) ?? EMPTY_MARKUPS
          }
          visible={
            mapBoxViewerStore.markupPrefs.supportsWorkflowMarkups &&
            mapBoxViewerStore.markupPrefs.showWorkflowMarkups
          }
        />
        {/* The session's own markups, full color. While an editor is attached it draws the
            shapes and feeds the text, and stays visible whatever the markup preference, as the
            editor does; otherwise these are the drafts (a create in flight: the save-time
            screenshot must not capture the drawing dimmed). */}
        <MarkupLayer
          isZoomScalingEnabled={
            mapBoxViewerStore.globeViewStyle.isZoomScalingEnabled
          }
          idPrefix={SESSION_MARKUP_PREFIX}
          ground={this.labelGround}
          markups={this.sessionDraftMarkups}
          editorText={this.markupSession?.editorText}
          visible={
            !!this.markupSession?.editorText.isAttached ||
            (mapBoxViewerStore.markupPrefs.supportsWorkflowMarkups &&
              mapBoxViewerStore.markupPrefs.showWorkflowMarkups)
          }
        />
        <GLWorkflowPinLabelLayer
          points={this.workflowPinLabelData.points}
          logoUrls={this.workflowPinLabelData.logoUrls}
          // The "Show workflow pin labels" Map Settings toggle; off keeps the selected ones.
          showUnselected={
            !mapBoxViewerStore.globeViewStyle.shouldHideWorkflowPinLabels
          }
          pinLayerIds={WORKFLOW_PIN_INTERACTIVE_LAYER_IDS}
          allowOverlap={mapBoxViewerStore.globeViewStyle.isLabelOverlapEnabled}
          hoveredCoordKey={this.hoveredPinCoordKey}
          ground={this.labelGround}
          planFootprints={this.planFootprints}
          isZoomScalingEnabled={
            mapBoxViewerStore.globeViewStyle.isZoomScalingEnabled
          }
        />
        {/* Shared before workflow, so workflow pins place first — the order a click
            resolves them in (PIN_HIT_PASSES). Captions are held under the locations
            by enforceShapeLayerOrder, whatever their mount order. */}
        <GLSharedPinLayer
          points={this.sharedPinData.points}
          isZoomScalingEnabled={
            mapBoxViewerStore.globeViewStyle.isZoomScalingEnabled
          }
          onHoverChange={mapBoxViewerStore.setOverlayPointerHovered}
          interactive={!this.markupSession || this.markupSession.isEditorIdle}
        />
        <GLWorkflowPinLayer
          points={this.workflowPinData.points}
          isZoomScalingEnabled={
            mapBoxViewerStore.globeViewStyle.isZoomScalingEnabled
          }
          onHoverChange={mapBoxViewerStore.setOverlayPointerHovered}
          onHoveredCoordKeyChange={this.setHoveredPinCoordKey}
          // The label names the same group, so pointing at it is pointing at the pin.
          extraInteractiveLayerIds={WORKFLOW_PIN_LABEL_LAYER_IDS}
          interactive={!this.markupSession || this.markupSession.isEditorIdle}
        />
        {this.localProps.shouldRenderSwipeableCards && (
          <StackDrawer
            isOpen={this.stack.isShown}
            onClosed={this.stack.forget}
            {...this.stack.listProps}
          />
        )}
        {this.renderPreSelectedPin()}
        {this.renderWorkflowCreationPin()}
        {this.renderGlobePopups}
      </>
    )
  }

  // Outside the map container, beside the Composer's overlay: a press in there reaches
  // onMouseDown, which dismisses the bubble mid-click. Not in renderGlobePopups, which
  // mobile skips in favor of swipeable cards.
  private renderCreateWorkflowBubble(): JSX.Element {
    // Not isWorkflowCreationAvailable: it flips on hand-off, unmounting the bubble mid-close.
    if (!this.canEditMarkupsOnMap) {
      return null
    }
    return (
      <WorkflowTypeCreateBubble
        open={this.localProps.store.shouldShowCreateWorkflowBubble}
        allowedTypeIds={this.mapCreatableWorkflowTypeIds}
        onSelect={this.startWorkflowCreationFromBubble}
        onClose={this.closeCreateWorkflowBubble}
        mapBoxViewerStore={this.localProps.mapBoxViewerStore}
        onCenterAboveSheet={this.markupSession.centerCoordinatesAboveSheet}
      />
    )
  }

  @computed
  private get mapCreatableWorkflowTypeIds(): string[] {
    return this.mapCreatableWorkflowTypes.map(type => type.id)
  }

  @action.bound
  private closeCreateWorkflowBubble(): void {
    this.localProps.store.setShouldShowCreateWorkflowBubble(false)
    this.localProps.state.preSelectedSiteLocation = null
  }

  // The ghost for a workflow being placed: the seed either carries its own coordinate or borrows
  // its location marker's. One pin either way, so the gates are stated once.
  private renderPreSelectedPin(): JSX.Element {
    const { preSelectedSiteLocation } = this.localProps.state
    const session = this.markupSession
    if (
      !preSelectedSiteLocation ||
      // Move mode's fixed centre pin stands in.
      session?.isMovingPin ||
      // Handed off, renderWorkflowCreationPin draws the movable pin instead.
      session?.isHandedOff ||
      session?.isInteracting
    ) {
      return null
    }

    const { preciseLocation } = preSelectedSiteLocation
    const anchorCoordinates = preciseLocation
      ? null
      : (this.localProps.mapBoxViewerStore.displayedGlobeViewItems ?? []).find(
          i => i.dataObject?.id === preSelectedSiteLocation.id,
        )?.sitemapItem?.coordinates
    const coordinates = preciseLocation ?? anchorCoordinates
    if (!coordinates) {
      return null
    }

    return (
      <Marker
        longitude={coordinates.longitude}
        latitude={coordinates.latitude}
        anchor="center"
        className="tw:z-[1]"
      >
        {/* Borrowed coordinates sit on a marker, so the pin lifts clear of it. */}
        <DomPin
          overlay
          fill={DRAFT_PIN_FILL}
          liftsAboveMarker={!preciseLocation}
          glyph={({ size, color }) => (
            <WorkflowGlyph
              iconName={this.preSelectedWorkflowType?.iconName}
              size={size}
              color={color}
            />
          )}
        />
      </Marker>
    )
  }

  @computed
  private get creationWorkflowType(): WorkflowType {
    return resolveCreationWorkflowType(
      this.localProps.workflowTypesStore,
      this.markupSession?.creationWorkflowTypeId ??
        this.markupSession?.handedOffWorkflowTypeId,
      this.mapCreateWorkflowType,
    )
  }

  @computed
  private get preSelectedWorkflowType(): WorkflowType {
    // preSelectedPermitTypeId outlives the form, so it only counts while one is open.
    const typeId =
      this.markupSession?.creationWorkflowTypeId ??
      (this.localProps.isPermitCreationActive
        ? this.localProps.state.preSelectedPermitTypeId
        : null)
    return typeId
      ? this.localProps.workflowTypesStore.getPermitTypeById(typeId)
      : null
  }

  // Hidden while moving — the overlay's fixed center pin stands in (STX1-686).
  private renderWorkflowCreationPin(): JSX.Element {
    const session = this.markupSession
    // Handed off, the pin is still this map's to show and move.
    if (!session?.pinCoordinates || session.isMovingPin) {
      return null
    }
    const { latitude, longitude } = session.pinCoordinates
    // Submitting is when the report screenshot is read, and it is read off the GL canvas, which
    // a DOM marker is not part of. The draft pin is that shot's subject, so for the duration it
    // draws as the map's own raster — which also keeps the Move badge out of the image.
    if (session.phase === 'submitting') {
      return (
        <GLWorkflowPinMarker
          sourceId={DRAFT_PIN_SOURCE_ID}
          layerId={DRAFT_PIN_LAYER_ID}
          latitude={latitude}
          longitude={longitude}
          iconName={this.creationWorkflowType?.iconName}
        />
      )
    }
    return (
      <DomMovablePin
        latitude={latitude}
        longitude={longitude}
        iconName={this.creationWorkflowType?.iconName}
        fill={DRAFT_PIN_FILL}
        canMove
        badgeOnly
        onMove={session.startMovingPin}
      />
    )
  }

  // Stable identity is load-bearing: renderGlobeViewChildren re-runs every camera
  // frame; the pin layer's React.memo relies on this computed keeping its reference.
  @computed
  private get pinnedWorkflows(): Workflow[] {
    return filterPinnedWorkflows(
      this.workflowsOnMap,
      this.workflowPinCoordOverrides,
    )
  }

  // Workflows and deliveries shared in from another project. They carry their location as a
  // maps link rather than a local item, so they group on their own coordinates.
  @computed
  private get sharedPinData(): {
    points: IWorkflowPinPoint[]
    groupsByCoordKey: Map<string, ISharedPinGroup>
  } {
    return buildSharedPinPoints(
      this.localProps.logisticsMapStore.filteredSharedPermits,
      // Shared deliveries were never drawn on this map: GlobeViewItems only ever got
      // shouldRenderPermits from here. The delivery maps mount their own set.
      [],
      { selectedCoordKey: this.selectedSharedCoordKey },
    )
  }

  @computed
  private get markupLabelPins(): IMarkupLabelPins {
    return {
      points: this.workflowPinData.points,
      isZoomScalingEnabled:
        this.localProps.mapBoxViewerStore.globeViewStyle.isZoomScalingEnabled,
    }
  }

  @computed
  private get workflowPinData(): {
    points: IWorkflowPinPoint[]
    workflowsByCoordKey: Map<string, Workflow[]>
  } {
    const { workflowTypesStore, eventsStore, shouldRenderSwipeableCards } =
      this.localProps
    const { selectedWorkflows = [], displayedPermitId } = this.localProps.store

    return buildWorkflowPinPoints(this.pinnedWorkflows, {
      coordOverrides: this.workflowPinCoordOverrides,
      getIconName: p => p.getIconNameOfPermitType(workflowTypesStore),
      isAssignedToUser: p => p.isAssignedToUser(eventsStore.appState.user),
      isAnchorCircleShown: id =>
        this.localProps.store.anchorCircleShownIds.has(id),
      isPinSelected: group =>
        isMapPinSelected({
          shouldRenderSwipeableCards,
          swipeableItemId: this.swipeableItemId,
          swipeableEntities: group,
          selectedEntities: selectedWorkflows,
          displayedEntityId: displayedPermitId,
        }),
    })
  }

  private markupAnchorFor(
    permitId?: string,
  ): IGeoJson2DGeographicCoordinates | undefined {
    // Guard null anchor + missing id: otherwise undefined === undefined is truthy and
    // derefs a null markupPopupAnchor.coord (crashes when nothing is selected).
    if (!this.markupPopupAnchor || !permitId) {
      return undefined
    }
    return this.markupPopupAnchor.permitId === permitId
      ? this.markupPopupAnchor.coord
      : undefined
  }

  // The workflow's pin may be on a different globe, so anchor its popup at the markup.
  @action.bound
  private selectPermitFromMarkupLabel(
    markupId: string,
    labelPoint: { longitude: number; latitude: number },
  ): void {
    const markupsStore = this.localProps.markupsStore
    const markup = markupsStore?.byId.get(markupId)
    const permit = markup
      ? markupsStore?.visibleWorkflows?.get(markup.sitePermitId)
      : null
    if (!permit || !markup?.coordinates?.[0]) return
    this.onPreciseLocationPermitClick([permit])
    // Anchor the popup at the label the user clicked: a text markup's label stands above
    // its box, where it keeps clear of the pins, not on the raw markup point.
    this.markupPopupAnchor = {
      permitId: permit.id,
      coord: labelPoint,
      offset: MARKUP_LABEL_POPUP_OFFSET_PX,
    }
  }

  @action.bound
  private onPreciseLocationPermitClick(permits: Workflow[]): void {
    const firstPermit = permits[0]
    if (!firstPermit) return

    // Drop any stale label anchor; selectPermitFromMarkupLabel re-sets it after.
    this.markupPopupAnchor = null

    if (this.localProps.shouldRenderSwipeableCards) {
      this.swipeableItemId = firstPermit.id
    }

    // Deliberately NOT writing selectedMarkerItemId — selection is carried by
    // activateWorkflows; deselection runs via the popup's closeOnClick.
    this.localProps.store.activateWorkflows(permits)
  }

  // The popup lists workflow models, and a shared DTO converts to one — the same conversion
  // the list, the gantt and the calendar already use for shared rows.
  @computed
  private get selectedSharedGroup(): ISharedPinGroup {
    return this.sharedPinData.groupsByCoordKey.get(this.selectedSharedCoordKey)
  }

  @computed
  private get selectedSharedWorkflows(): Workflow[] {
    return (this.selectedSharedGroup?.workflows || []).map(dto =>
      Workflow.fromSharedPermitDto(dto),
    )
  }

  @action.bound
  private selectSharedGroup(coordKey: string): void {
    this.localProps.store.deselectAll()
    this.selectedSharedCoordKey = coordKey
  }

  // Every workflow in a group comes from the same coordinate but not necessarily the same
  // project, so the header names the first one's — the same one the popup lists first.
  @computed
  private get selectedSharedProjectName(): string {
    const dto = this.selectedSharedGroup?.workflows[0]
    return dto
      ? getSharedProjectName(
          dto.projectUrl,
          this.localProps.projectsStore,
          dto.projectFromId,
        )
      : null
  }

  @action.bound
  private deselectSharedGroup(): void {
    this.selectedSharedCoordKey = null
  }

  @computed
  private get selectedSharedPoint(): IWorkflowPinPoint {
    return this.sharedPinData.points.find(p => p.isSelected)
  }

  // A row opens the shared entity's own panel, which is where a click landed before.
  private openSharedWorkflow = (workflow: Workflow): void => {
    this.selectSharedEntity(workflow.id)
  }

  private selectSharedEntity = (entityId: string) => {
    const entity = this.localProps.logisticsStore.getSharedFormById(entityId)
    this.localProps.logisticsStore.setSelectedSharedEntity(entity)
  }

  @action.bound
  private selectSitemapItem(item: MapViewItemBase, y?: number, x?: number) {
    const { selectItem } = this.localProps.store

    if (!item.isDataLess) {
      this.swipeableItemId = null
      selectItem(item, y, x)
    }
  }

  private renderSitemapChildren = (width: number, height: number) => {
    const {
      textboxesCache,
      store: { selectItem, displayedSitemapItems },
      logisticsStore: { startDate, endDate },
    } = this.localProps

    return (
      <>
        <KonvaSitemapItems
          className="unclickable-element"
          items={displayedSitemapItems || []}
          containerWidth={width}
          containerHeight={height}
          textboxesCache={textboxesCache}
          onSelectItem={selectItem}
          selectedStartDate={startDate}
          selectedEndDate={endDate}
        />
        <SitemapElementsWrapper>
          {this.workflowsOnMap.map(this.renderPermit.bind(this, width, height))}
        </SitemapElementsWrapper>
      </>
    )
  }

  @action
  private onSitemapTouch = () => {
    this.localProps.store.deselectAll()
    this.swipeableItemId = null
  }

  private renderPermit(width: number, height: number, permit: Workflow) {
    const {
      shouldRenderSwipeableCards,
      workflowTypesStore,
      eventsStore: { appState },
    } = this.localProps
    const { getPermitLocationPositions, getRelatedItems } =
      this.localProps.store

    const items = getRelatedItems(permit.locationsWithEquipment)

    if (!items) {
      return
    }

    return items.map((item, lIndex) => {
      const { position } = item.sitemapItemProperties.iconProperties
      const samePositionPermits = this.workflowsOnMap.filter(p => {
        return getPermitLocationPositions(p).some(
          l => l.x === position.x && l.y === position.y,
        )
      })

      const samePositionIdx = samePositionPermits.indexOf(permit)

      if (samePositionIdx > 0) {
        return
      }
      const text =
        samePositionPermits.length > 1
          ? samePositionPermits.length.toString()
          : permit.code
      const x =
        (width * position.x) / MAX_PERCENT +
        samePositionIdx * SAME_POSITION_OFFSET
      const y = (height * position.y) / MAX_PERCENT

      const isSelected = isMapPinSelected({
        shouldRenderSwipeableCards,
        swipeableItemId: this.swipeableItemId,
        swipeableEntities: samePositionPermits,
        storeEntities: [permit],
        selectedEntities: this.localProps.store.selectedWorkflows ?? [],
        displayedEntityId: this.localProps.store.displayedPermitId,
      })

      // The same grouping rule the globe's pins use: one colour speaks for the group only
      // while the group agrees, and any late member makes the pin late.
      const groupStatus = statusForGroup(samePositionPermits)
      const isGroupLate = samePositionPermits.some(p => p.isLate)
      const groupIconName = iconNameForGroup(samePositionPermits, p =>
        p.getIconNameOfPermitType(workflowTypesStore),
      )
      const shouldShowPinAsAssigned = samePositionPermits.some(p =>
        p.isAssignedToUser(appState.user),
      )

      return (
        <KonvaPin
          text={text}
          fill={getWorkflowStatusPinFill({
            status: groupStatus.status,
            isLate: isGroupLate,
            isMixed: groupStatus.isMixed,
            isSelected,
          })}
          ink={WORKFLOW_PIN_INK}
          glyph={getWorkflowGlyphPaths(groupIconName)}
          isAssigned={shouldShowPinAsAssigned}
          isSelected={isSelected}
          shouldRenderCircle={samePositionPermits.length > 1}
          key={permit.id + lIndex}
          x={x}
          y={y}
          offsetY={OFFSET_Y}
          onTouchEnd={this.onPermitPillTouch.bind(
            this,
            samePositionPermits,
            item,
          )}
          onClick={this.onPermitPillClick.bind(this, samePositionPermits, item)}
        />
      )
    })
  }

  @action
  private onPermitPillTouch(
    permits: Workflow[],
    item: MapViewItemBase,
    event: KonvaEventObject<TouchEvent>,
  ) {
    const { shouldRenderSwipeableCards, store } = this.localProps

    if (shouldRenderSwipeableCards) {
      this.swipeableItemId = permits[0]?.id
    }

    store.selectWorkflows(permits, item)

    event.cancelBubble = true
  }

  private onPermitPillClick(
    permits: Workflow[],
    item: MapViewItemBase,
    event: KonvaEventObject<MouseEvent>,
  ) {
    const { store } = this.localProps
    store.selectWorkflows(permits, item, event.evt.layerY, event.evt.layerX)
    event.cancelBubble = true
  }

  @action.bound
  private setContainerRef(ref: HTMLElement) {
    this.containerRef = ref
  }

  @computed
  public get workflowsOnMap(): Workflow[] {
    const {
      logisticsMapStore: { filteredPermits },
      store: {
        globeViewControlStore: { selectedGlobeViewId },
      },
    } = this.localProps

    return filterWorkflowsOnMap(
      this.localProps.store.pinAnchorContext,
      filteredPermits,
      selectedGlobeViewId,
    )
  }

  // Anchor each pin at its nearest displayed ancestor, else the project catch-all; GL declutter
  // alone decides visibility.
  @computed
  public get workflowPinCoordOverrides(): Map<
    string,
    IWorkflowPinCoordOverride
  > {
    return buildWorkflowPinCoordOverrides(
      this.localProps.store.pinAnchorContext,
      this.workflowsOnMap,
    )
  }

  @action.bound
  private selectSwipeableItem(id: string): void {
    this.swipeableItemId = id
  }

  // whitespace-normal because the carousel's track is nowrap and the cards inherit it; the rest
  // is the scroll room a long workflow card needs inside a fixed strip.
  private renderSwipeableCard = (workflow: Workflow): JSX.Element => {
    const { openPermit } = this.localProps
    return (
      <div className="tw:whitespace-normal tw:[&_.workflow-card]:max-h-[600px] tw:[&_.workflow-card]:overflow-y-auto! tw:[&_.workflow-card]:overflow-x-hidden!">
        <PermitCard permit={workflow} onClick={openPermit} />
      </div>
    )
  }
}
